import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Input, OnDestroy, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatTooltipModule } from '@angular/material/tooltip';
import * as echarts from 'echarts';
import { applyRoleVisibilityToOption } from '../../utils/chart-role-visibility.utils';
import { NgxEchartsModule } from 'ngx-echarts';
import { Subscription } from 'rxjs';
import { ConfigService } from '../../shared-services/config.service';
import { ChartFilterHandlerService } from '../../shared-services/chart-filter-handler.service';
import { ResponsiveLayoutService } from '../../shared-services/responsive-layout.service';
import { ThemeService } from '../../shared-services/theme.service';
import { buildConfigurableAxisLabel, formatTooltipValue, getTooltipLabel } from '../../utils/chart.utils';
import { CapitalCasePipe } from '../../pipes/capital-case.pipe';

@Component({
  selector: 'app-quad-bubble-chart',
  standalone: true,
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgxEchartsModule,
    CommonModule,
    MatIconModule,
    MatTooltipModule,
    CapitalCasePipe,
  ],
  templateUrl: './quad-bubble-chart.component.html',
  styleUrl: './quad-bubble-chart.component.scss',
})
export class QuadBubbleChartComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;

  get resolvedHeight(): string {
    return this.responsiveLayoutService.resolveWidgetHeight(this.data);
  }

  private configService = inject(ConfigService);
  private chartFilterHandler = inject(ChartFilterHandlerService);
  protected responsiveLayoutService = inject(ResponsiveLayoutService);
  private themeService = inject(ThemeService);
  protected reflowSub?: Subscription;
  private themeSub?: Subscription;
  private dataSub?: Subscription;
  protected chart!: echarts.ECharts;
  private lastChartData?: any;

  ngAfterViewInit(): void {
    const chartDom = document.getElementById(this.data.widgetId);
    if (chartDom) {
      this.chart = echarts.init(chartDom, 'dvm');
      this.loadAndRenderChart();
      window.addEventListener('resize', this.resizeChart);
    }

    this.reflowSub = this.responsiveLayoutService.reflow$.subscribe(() => {
      this.chart?.resize();
    });

    this.themeSub = this.themeService.theme$.subscribe(() => {
      if (!this.chart) return;
      const dom = document.getElementById(this.data.widgetId);
      if (!dom) return;
      this.chart.dispose();
      this.chart = echarts.init(dom, 'dvm');
      this.renderChart(this.lastChartData || this.resolveChartData(this.data));
    });
  }

  protected loadAndRenderChart(): void {
    if (!this.chart) return;

    const apiUrl = String(this.data?.apiUrl || '').trim();
    const method = String(this.data?.method || 'get').toLowerCase() as 'get' | 'post';

    if (apiUrl) {
      this.dataSub?.unsubscribe();
      const isLocalAsset = apiUrl.startsWith('assets') || apiUrl.startsWith('/assets');
      const request$ = isLocalAsset
        ? this.configService.getData(apiUrl)
        : this.configService.getConfig(apiUrl, this.data, undefined, method);

      this.dataSub = request$.subscribe({
        next: (response: any) => this.renderChart(this.resolveChartData(response)),
        error: () => this.renderChart(this.resolveChartData(this.data)),
      });
      return;
    }

    this.renderChart(this.resolveChartData(this.data));
  }

  protected renderChart(chartData?: any): void {
    if (chartData) {
      this.lastChartData = chartData;
    }

    const theme = this.themeService.resolve(this.data?.themeOverrides);
    const fs = this.responsiveLayoutService.getFontScale();
    const sc = (px: number) => Math.max(1, Math.round(px * fs));

    const raw = chartData;
    const clusterCount = this.data.clusterCount || 4;
    const symbolSize = this.data.symbolSize || 15;
    const points: any[] = [
      ...this.buildQuadrantPoints(raw?.q1 ?? [], 0),
      ...this.buildQuadrantPoints(raw?.q2 ?? [], 1),
      ...this.buildQuadrantPoints(raw?.q3 ?? [], 2),
      ...this.buildQuadrantPoints(raw?.q4 ?? [], 3),
    ];

    const dimensionClusterIndex = 2;
    const COLOR_ALL = ['#37A2DA', '#e06343', '#37a354', '#b55dba', '#b5bd48', '#8378EA', '#96BFFF'];

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'item',
        backgroundColor: theme.tooltip?.bg || '#fff',
        borderColor: theme.tooltip?.borderColor || '#ccc',
        textStyle: {
          color: theme.tooltip?.textColor || '#333',
          fontSize: theme.tooltip?.fontSize || 13,
        },
        formatter: (() => {
          const tooltipCfg = this.data?.chartConfig?.tooltip;
          if (tooltipCfg?.fields?.length) {
            return (params: any) => {
              const d = params.data || {};
              const lines: string[] = [];
              for (const field of tooltipCfg.fields) {
                const label = getTooltipLabel(field, tooltipCfg.autoCapitalizeKeys !== false);
                const idx = parseInt(field.key, 10);
                const rawValue = !isNaN(idx)
                  ? (d?.value?.[idx] ?? d?.[field.key])
                  : (d?.[field.key] ?? d?.value?.[idx]);
                lines.push(`<b>${label}:</b> ${formatTooltipValue(rawValue, field)}`);
              }
              return lines.join('<br/>');
            };
          }
          return (params: any) => {
            const d = params.data || {};
            const rawValue = d.value || [];
            return `x: ${d.x ?? rawValue[0] ?? '-'}, y: ${d.y ?? rawValue[1] ?? '-'}`;
          };
        })(),
      },
      visualMap: {
        type: 'piecewise',
        dimension: dimensionClusterIndex,
        selectedMode: 'multiple',
        orient: 'horizontal',
        left: 'center',
        top: sc(4),
        itemGap: sc(10),
        itemWidth: sc(14),
        itemHeight: sc(12),
        textStyle: { fontSize: sc(11) },
        pieces: Array.from({ length: clusterCount }, (_, i) => ({
          value: i,
          label: `Cluster ${i + 1}`,
          color: COLOR_ALL[i],
        })),
      },
      xAxis: {
        min: this.data.xAxis?.min ?? 0,
        max: this.data.xAxis?.max ?? 10,
        splitLine: { show: false },
        axisLabel: { fontSize: sc(10), hideOverlap: true },
      },
      yAxis: {
        min: this.data.yAxis?.min ?? 0,
        max: this.data.yAxis?.max ?? 10,
        splitLine: { show: false },
        axisLabel: {
          fontSize: sc(10),
          hideOverlap: true,
          ...((buildConfigurableAxisLabel(this.data?.chartConfig?.yAxis) as any) || {}),
        },
      },
      grid: { left: '3%', right: '5%', top: '18%', bottom: '10%', containLabel: true },
      series: [
        {
          type: 'scatter',
          data: points,
          encode: { x: 0, y: 1, tooltip: [0, 1] },
          symbolSize: (value: any) => {
            const bubbleSize = Array.isArray(value) ? value[3] : undefined;
            return Number(bubbleSize || symbolSize);
          },
          itemStyle: { borderColor: 'transparent' },
          markArea: {
            silent: true,
            itemStyle: { opacity: 0.2 },
            data: this.getQuadrantAreas(),
          },
        },
      ],
    };

    applyRoleVisibilityToOption(option, this.data);

    this.chart.setOption(option, true);
    this.setupBubbleClickEvent();
  }

  protected buildQuadrantPoints(points: any[], quadrantIndex: number): any[] {
    return points.map((point, index) => this.normalizeQuadrantPoint(point, quadrantIndex, index));
  }

  protected normalizeQuadrantPoint(point: any, quadrantIndex: number, index: number): any {
    const quadrant = `Q${quadrantIndex + 1}`;
    const isArrayPoint = Array.isArray(point);
    const x = Number(isArrayPoint ? point[0] : point?.x ?? point?.value?.[0] ?? 0);
    const y = Number(isArrayPoint ? point[1] : point?.y ?? point?.value?.[1] ?? 0);
    const key = String(
      point?.key ??
        point?.id ??
        point?.name ??
        point?.label ??
        `${quadrant.toLowerCase()}-${index + 1}`,
    );
    const label = String(point?.label ?? point?.name ?? point?.title ?? key);
    const size = Number(
      point?.size ??
        point?.symbolSize ??
        (isArrayPoint ? point?.[2] : point?.value?.[3]) ??
        this.data?.symbolSize ??
        15,
    );

    return {
      name: label,
      key,
      label,
      quadrant,
      quadrantIndex,
      x,
      y,
      size,
      value: [x, y, quadrantIndex, size],
    };
  }

  protected getQuadrantAreas(): any[] {
    return (this.data.quadrantBackgrounds || []).map((quad: any) => ([
      {
        name: '',
        xAxis: quad.xAxis,
        yAxis: quad.yAxis,
        itemStyle: { color: quad.color },
      },
      {
        xAxis: quad.x2,
        yAxis: quad.y2,
      },
    ]));
  }

  protected resolveChartData(source: any): any {
    const seriesPath = String(this.data?.series || '').trim();
    if (seriesPath) {
      const resolved = this.resolvePath(source, seriesPath);
      if (resolved !== undefined && resolved !== null) {
        return resolved;
      }
    }

    return source?.chartData ?? source?.data?.chartData ?? source?.data ?? source;
  }

  protected resolvePath(obj: any, path: string): any {
    return path.split('.').reduce((o, key) => (o ? o[key] : undefined), obj);
  }

  protected setupBubbleClickEvent(): void {
    if (!this.chart) return;

    this.chart.off('click');
    this.chart.on('click', (params: any) => {
      if (params?.componentType !== 'series') return;

      const point = params?.data || {};
      const raw = Array.isArray(point?.value) ? point.value : point?.value ?? point;
      const quadrantIndex = Number(point?.quadrantIndex ?? raw?.[2]);
      const quadrant = String(point?.quadrant ?? (Number.isFinite(quadrantIndex) ? `Q${quadrantIndex + 1}` : ''));
      const filterKey = String(this.data?.filterKey || 'quadrant').trim();
      if (!filterKey || !quadrant) return;

      const payload = {
        [filterKey]: quadrant,
        key: point?.key ?? point?.name,
        label: point?.label ?? point?.name,
        x: point?.x ?? raw?.[0],
        y: point?.y ?? raw?.[1],
        quadrant,
        quadrantIndex,
      };

      this.chartFilterHandler.updateFilterSelection(
        this.data,
        payload,
        {
          ...point,
          ...payload,
          cluster: quadrantIndex,
        },
      );
    });
  }

  protected resizeChart = () => {
    this.chart?.resize();
  };

  ngOnDestroy(): void {
    this.reflowSub?.unsubscribe();
    this.themeSub?.unsubscribe();
    this.dataSub?.unsubscribe();
    window.removeEventListener('resize', this.resizeChart);
    this.chart?.dispose();
  }
}
